import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { DeviceCapabilitySchema, ModelInterfacesWriteSchema } from '../assets/access.js';
import { AssetCategorySchema } from '../assets/categories.js';
import { BatteryChemistrySchema, BatteryTechnologySchema } from '../common/battery.js';
// Products catalog (ADR-065) — request shapes. The spec-8 keys are accepted on
// every write shape; WHICH keys are legal for the row's category is descriptor-
// scoped and service-enforced (categories.ts modelSpecColumns) — the wire stays
// one shape, the rule lives in one place.
// The device half on LOCAL rows (the four-layer law): declared, enum-gated to
// the known tokens (canonical rows are loader-written and may carry newer
// tokens; locals tick from this release's list). `interfaces` replaces the
// whole facet on update; the service normalizes both (dedupe/registry order ·
// the absence law) before storage.
const deviceHalfFields = {
    capabilities: z.array(DeviceCapabilitySchema).max(14).optional(),
    interfaces: ModelInterfacesWriteSchema.optional(),
};
const specFields = {
    power_kw: z.number().positive().nullable().optional(),
    capacity_kva: z.number().positive().nullable().optional(),
    capacity_ah: z.number().int().positive().nullable().optional(),
    voltage_v: z.number().positive().nullable().optional(),
    watt_peak_w: z.number().int().positive().nullable().optional(),
    cooling_kw: z.number().positive().nullable().optional(),
    tank_l: z.number().int().positive().nullable().optional(),
    battery_chemistry: BatteryChemistrySchema.nullable().optional(),
    battery_technology: BatteryTechnologySchema.nullable().optional(),
    // The day-1 graduated product fact (the lifecycle law): the wizard prefills
    // warranty_end_on = acquired + months, editable.
    warranty_months: z.number().int().positive().max(240).nullable().optional(),
};
// Local-row birth (asset.models:manage) — the picker's not-found end. Tiny by
// design: name the product as the field names it; facts fill in via curation.
export const CreateAssetModelSchema = z
    .object({
    category: AssetCategorySchema,
    manufacturer: z.string().trim().min(1).max(64).nullable().optional(),
    name: z.string().trim().min(1).max(96),
    series: z.string().trim().min(1).max(48).nullable().optional(),
    part_number: z.string().trim().min(1).max(64).nullable().optional(),
    description: z.string().trim().min(1).max(2000).nullable().optional(),
    is_network_device: z.boolean().nullable().optional(),
    ...deviceHalfFields,
    ...specFields,
})
    .strict();
// Curation patch (local rows only — canonical is read-only on the instance;
// category is immutable at v1: assets reference the row under the aggregate-
// unit rule, and a category flip would strand them).
export const UpdateAssetModelSchema = z
    .object({
    ...stampedBody,
    manufacturer: z.string().trim().min(1).max(64).nullable().optional(),
    name: z.string().trim().min(1).max(96).optional(),
    series: z.string().trim().min(1).max(48).nullable().optional(),
    part_number: z.string().trim().min(1).max(64).nullable().optional(),
    description: z.string().trim().min(1).max(2000).nullable().optional(),
    is_network_device: z.boolean().nullable().optional(),
    // Availability — locals only ("not offered for new use"; the enable/disable
    // door). Canonical availability is catalog-owned (the import writes it from
    // the record's `withdrawn` flag) — the canonical guard 409s this PATCH.
    is_disabled: z.boolean().optional(),
    ...deviceHalfFields,
    ...specFields,
})
    .strict();
// Instance-wide picker/dedup search (deliberate — ADR-065: dedup depends on
// findability). Capped list + has_more; no pagination.
export const AssetModelSearchQuerySchema = z
    .object({
    q: z.string().trim().max(96).optional(),
    category: AssetCategorySchema.optional(),
})
    .strict();
// Query-string booleans parse the WORD, never Boolean("false") — z.coerce
// would read "false" as true (the classic footgun; caught live on the catalog
// page's generics filter).
const queryBool = (def) => z.preprocess((v) => (v === undefined ? def : v === true || v === 'true'), z.boolean());
// Front-page list — pagination: none (the
// CONVENTIONS triggers: instance-bounded curated content, human-scale insertion
// pressure, a pager-less UI). The picker's /search keeps its own 20-row cap.
export const AssetModelListQuerySchema = z
    .object({
    q: z.string().trim().max(96).optional(),
    category: AssetCategorySchema.optional(),
    tier: z.enum(['canonical', 'local']).optional(),
    include_disabled: queryBool(false),
    // The catalog surface browses PRODUCTS — generic placeholder rows are the
    // rung-2 escape hatch, not products (architect-ruled at the store eyeball);
    // default true keeps the pre-surface behavior for other callers.
    include_generics: queryBool(true),
})
    .strict();
// Merge (sysadmin-grade at v1 — ADR-065): winner in the URL, loser in the body.
// The loser must be a LOCAL row (canonical rows are read-only on the instance).
export const MergeAssetModelSchema = z
    .object({ loser_id: z.string().min(1).max(16) })
    .strict();
// ── the PRODUCTS import/export dataset (the local catalog's file twin) ─────────
// One row = one product at the locked base-product grain. The match key is the
// product's NAME within its MANUFACTURER (composite, CI — `matchKeyOf`); a row
// naming a CANONICAL product is a skip (the canonical tier is the catalog's,
// read-only on the instance — the anti-junk rail), a new pair is a LOCAL birth
// in the project, a known local is curated. The device half is flat cells:
// `capabilities` as a token list (comma-separated), the interfaces facet as
// six plain columns. Spec cells apply per the category charter (the same law
// the wizard enforces); blank = untouched.
const csvOf = (inner) => z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(inner)).optional();
export const AssetModelImportRowSchema = z
    .object({
    name: z.string().trim().min(1).max(96),
    manufacturer: z.string().trim().min(1).max(64).optional(),
    category: AssetCategorySchema.optional(), // required to create; immutable on a match
    series: z.string().trim().min(1).max(48).optional(),
    part_number: z.string().trim().min(1).max(64).optional(),
    description: z.string().trim().min(1).max(2000).optional(),
    is_network_device: z.boolean().optional(),
    capabilities: z.string().trim().min(1).max(512).optional(), // comma-separated capability tokens
    sims: z.number().int().min(0).max(4).optional(),
    esim: z.boolean().optional(),
    ethernet_ports: z.number().int().min(0).optional(),
    ethernet_wan: z.number().int().min(0).optional(),
    rs232: z.number().int().min(0).optional(),
    rs485: z.number().int().min(0).optional(),
    fronts: z.boolean().optional(),
    power_kw: z.number().positive().optional(),
    capacity_kva: z.number().positive().optional(),
    capacity_ah: z.number().int().positive().optional(),
    voltage_v: z.number().positive().optional(),
    watt_peak_w: z.number().int().positive().optional(),
    cooling_kw: z.number().positive().optional(),
    tank_l: z.number().int().positive().optional(),
    battery_chemistry: BatteryChemistrySchema.optional(),
    battery_technology: BatteryTechnologySchema.optional(),
    warranty_months: z.number().int().positive().max(240).optional(),
    offered: z.boolean().optional(), // the local availability switch (false = not offered for new use)
})
    .strict();
// The catalog export — the page's own view keys (URL keys ARE the endpoint
// keys: category · tier · capability · usage · availability + `q`), judged
// through the shared lens (catalog/lens.ts).
export const ExportAssetModelsQuerySchema = z
    .object({
    format: z.enum(['xlsx', 'csv']).default('xlsx'),
    lang: z.enum(['en', 'fr', 'ar']).default('en'),
    q: z.string().trim().max(96).optional(),
    category: csvOf(AssetCategorySchema),
    capability: csvOf(z.string().trim().min(1).max(32)),
    usage: csvOf(z.enum(['inuse', 'unused'])),
    availability: csvOf(z.enum(['hidden'])),
})
    .strict();
// The catalog page's ONE-workbook export: PRODUCTS (the project's local tier —
// the import's twin) beside the GLOBAL CATALOG (the canonical tier, an
// export-only read-only sheet). The tier is the DATASET, never a carried
// filter key — which is why the export query above has none.
export const CATALOG_COMBINED_DATASETS = ['products', 'catalog'];
export const CatalogCombinedDatasetSchema = z.enum(CATALOG_COMBINED_DATASETS);
export const ExportAssetModelsCombinedQuerySchema = ExportAssetModelsQuerySchema.omit({ format: true })
    .extend({
    datasets: z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(CatalogCombinedDatasetSchema).min(1).max(2)),
})
    .strict();
